import React, { useEffect, Suspense } from "react"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import CircularProgress from "@mui/material/CircularProgress"
import Typography from "@mui/material/Typography"

import "./scss/style.scss"
import "./scss/3c-tokens.css" // design tokens + Outfit font (global)
import "./scss/3c-account.css" // account design system (scoped under .acct)
import "./scss/3c-auth.css" // auth pages (login etc.) scoped under .acct
import "./scss/3c-store.css" // storefront pages (products etc.) scoped under .acct
import "./scss/3c-landing.css" // landing page (own design system) scoped under .atl
import "./scss/3c-landing-v2.css" // new landing page (new-ui export) scoped under .atl2
import routes from "src/routes/dashboardRoutes"
import { FEATURES } from "./config/features"
// Persistent account/profile layout (chrome stays mounted across sidebar nav)
import ProfileShell from "./components/profile-chrome/ProfileShell"
import ScrollToTop from "./components/ScrollToTop"
import SupportWidget from "src/components/support/SupportWidget"

const loading = (
    <div className="h-100vh d-flex">
        <div className="d-flex flex-column m-auto">
            <CircularProgress />
            <Typography variant="caption" className="mt-3" color="primary">
                Loading...
            </Typography>
        </div>
    </div>
)

// Containers
const DefaultLayout = React.lazy(() => import("./layout/DefaultLayout"))

// Pages
const Login = React.lazy(() => import("./pages/login/Login"))
const SignUp = React.lazy(() => import("./pages/sign_up/SignUp"))
const Page404 = React.lazy(() => import("./pages/page404/Page404"))
const Page500 = React.lazy(() => import("./pages/page500/Page500"))
// The new-ui export. The previous landing page is still at ./pages/landing if
// it needs to be switched back.
const Landing = React.lazy(() => import("./pages/landing/v2/LandingV2"))
const Products = React.lazy(() => import("./pages/products/Products"))
const ProductDetail = React.lazy(() => import("./pages/products/ProductDetail"))
const ShoppingCart = React.lazy(() => import("./pages/shopping_cart/ShoppingCart"))
const Checkout = React.lazy(() => import("./pages/checkout/Checkout"))
const CreditCheckout = React.lazy(() => import("./pages/checkout/CreditCheckout"))
const Profile = React.lazy(() => import("./pages/profile/Profile"))
const MyOrders = React.lazy(() => import("./pages/profile/MyOrders"))
const OrderDetails = React.lazy(() => import("./pages/profile/OrderDetails"))
const RedemptionOrderDetails = React.lazy(() => import("./pages/profile/RedemptionOrderDetails"))
const PrizeOrderDetails = React.lazy(() => import("./pages/profile/PrizeOrderDetails"))
const CreditTransactions = React.lazy(() => import("./pages/profile/CreditTransactions"))
const MyReferrals = React.lazy(() => import("./pages/profile/MyReferrals"))
const TreasurePointTransactions = React.lazy(() => import("./pages/profile/TreasurePointTransactions"))
const VipPointTransactions = React.lazy(() => import("./pages/profile/VipPointTransactions"))
const AirTagPointTransactions = React.lazy(() => import("./pages/profile/ConceptPointTransactions"))
const MyTickets = React.lazy(() => import("./pages/profile/MyTickets"))
const NewTicket = React.lazy(() => import("./pages/profile/NewTicket"))
const TicketThread = React.lazy(() => import("./pages/profile/TicketThread"))

const AboutUs = React.lazy(() => import("./pages/about_us/AboutUs"))
const ContactUs = React.lazy(() => import("./pages/contact_us/ContactUs"))
const PrivacyPolicy = React.lazy(() => import("./pages/privacy_policy/PrivacyPolicy"))
const TermAndCondition = React.lazy(() => import("./pages/term_and_condition/TermAndCondition"))
const ForgotPassword = React.lazy(() => import("./pages/forgot-password/ForgotPassword"))
const ForgotPasswordSent = React.lazy(() =>
    import("./pages/forgot_password_sent/ForgotPasswordSent"),
)
const ResetPassword = React.lazy(() => import("./pages/reset_password/ResetPassword"))
const ResetPasswordSuccess = React.lazy(() =>
    import("./pages/reset_password_success/ResetPasswordSuccess"),
)
const ResetPasswordFail = React.lazy(() => import("./pages/reset_password_fail/ResetPasswordFail"))
const VerifyEmail = React.lazy(() => import("./pages/verify_email/VerifyEmail"))
const QrReload = React.lazy(() => import("./pages/qr_reload/QrReload"))
const ReloadCard = React.lazy(() => import("./pages/reload_card/ReloadCard"))

const RedemptionProducts = React.lazy(() => import("./pages/redemption/RedemptionProducts"))
const RedemptionProductDetail = React.lazy(() => import("./pages/redemption/RedemptionProductDetail"))

const CreditPackages = React.lazy(() => import("./pages/credit_packages/CreditPackages"))
const PaymentResult = React.lazy(() => import("./pages/payment_result/PaymentResult"))

const TreasureBox = React.lazy(() => import("./pages/treasure_box/TreasureBox"))

const HowItWorks = React.lazy(() => import("./pages/how_it_works/HowItWorks"))

const App = () => {
    useEffect(() => {
        // Handle chunk load errors by reloading the page once
        const handleChunkError = (event) => {
            if (event.reason?.name === 'ChunkLoadError' || event.reason?.message?.includes('Loading chunk')) {
                const hasReloaded = sessionStorage.getItem('chunk_failed');
                if (!hasReloaded) {
                    sessionStorage.setItem('chunk_failed', 'true');
                    window.location.reload();
                }
            }
        };

        window.addEventListener('unhandledrejection', handleChunkError);

        return () => {
            window.removeEventListener('unhandledrejection', handleChunkError);
        };
    }, []);

    return (
        <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={loading}>
                <Routes>
                    <Route exact path="/login" name="Login Page" element={<Login />} />
                    <Route exact path="/sign-up" name="Sign Up Page" element={<SignUp />} />
                    <Route exact path="/404" name="Page 404" element={<Page404 />} />
                    <Route exact path="/500" name="Page 500" element={<Page500 />} />
                    <Route exact path="/products" name="Products" element={<Products />} />
                    <Route
                        exact
                        path="/products/:id"
                        name="Product Detail"
                        element={<ProductDetail />}
                    />
                    <Route exact path="/cart" name="Shopping Cart" element={<ShoppingCart />} />
                    <Route exact path="/checkout" name="Checkout" element={<Checkout />} />
                    <Route exact path="/checkout/credits/:id" name="Credit Checkout" element={<CreditCheckout />} />
                    {/* Sidebar pages share a persistent layout — only the content swaps */}
                    <Route element={<ProfileShell />}>
                        <Route exact path="/profile" name="Profile" element={<Profile />} />
                        <Route exact path="/profile/orders" name="My Orders" element={<MyOrders />} />
                        <Route exact path="/profile/credit-transactions" name="Credit Transactions" element={<CreditTransactions />} />
                        <Route exact path="/profile/treasure-points" name="Mystery Draws and History" element={<TreasurePointTransactions />} />
                        {FEATURES.vipCampaign && (
                            <Route exact path="/profile/vip-points" name="VIP Points" element={<VipPointTransactions />} />
                        )}
                        <Route exact path="/profile/airtag-points" name="My Redemptions" element={<AirTagPointTransactions />} />
                        <Route exact path="/profile/referrals" name="My Referrals" element={<MyReferrals />} />
                        <Route exact path="/profile/support" name="Support Tickets" element={<MyTickets />} />
                        <Route exact path="/profile/support/new" name="New Support Ticket" element={<NewTicket />} />
                    </Route>
                    {/* Retired pages, merged into the three above. Kept as redirects so existing
                        bookmarks and shared links resolve instead of 404ing. */}
                    <Route exact path="/profile/redemptions" element={<Navigate to="/profile/airtag-points" replace />} />
                    <Route exact path="/profile/treasure-box-plays" element={<Navigate to="/profile/treasure-points" replace />} />
                    <Route exact path="/profile/referral-coins" element={<Navigate to="/profile/referrals" replace />} />
                    {/* Detail pages are standalone (their own centered, sidebar-less layout) */}
                    <Route exact path="/profile/orders/:id" name="Order Details" element={<OrderDetails />} />
                    <Route exact path="/profile/redemptions/:id" name="Redemption Order Details" element={<RedemptionOrderDetails />} />
                    {/* Tracking for a physical prize won from a Mystery Draw. */}
                    <Route exact path="/profile/prize-orders/:id" name="Prize Order Details" element={<PrizeOrderDetails />} />
                    {/* A ticket thread is standalone for the same reason an order is: the
                        conversation wants the full width, not a sidebar beside it. */}
                    <Route exact path="/profile/support/:id" name="Support Ticket" element={<TicketThread />} />
                    <Route exact path="/how-it-works" name="How It Works" element={<HowItWorks />} />
                    <Route exact path="/about-us" name="About Us" element={<AboutUs />} />
                    <Route exact path="/contact-us" name="Contact Us" element={<ContactUs />} />
                    <Route
                        exact
                        path="/privacy-policy"
                        name="Privacy Policy"
                        element={<PrivacyPolicy />}
                    />
                    <Route
                        exact
                        path="/term-and-condition"
                        name="Term and Condition"
                        element={<TermAndCondition />}
                    />
                    <Route
                        exact
                        path="/forgot-password"
                        name="Forgot Password"
                        element={<ForgotPassword />}
                    />
                    <Route
                        exact
                        path="/forgot-password-sent"
                        name="Forgot Password Sent"
                        element={<ForgotPasswordSent />}
                    />
                    <Route
                        path="/reset-password"
                        name="Reset Password"
                        element={<ResetPassword />}
                    />
                    <Route
                        exact
                        path="/reset-password-success"
                        name="Reset Password Success"
                        element={<ResetPasswordSuccess />}
                    />
                    <Route
                        exact
                        path="/reset-password-fail"
                        name="Reset Password Fail"
                        element={<ResetPasswordFail />}
                    />
                    <Route
                        exact
                        path="/verify-email"
                        name="Verify Email"
                        element={<VerifyEmail />}
                    />
                    <Route
                        exact
                        path="/qr/:transactionId"
                        name="QR Code Credit Reload"
                        element={<QrReload />}
                    />
                    <Route
                        exact
                        path="/reload-card"
                        name="Top Up with Card"
                        element={<ReloadCard />}
                    />
                    <Route
                        exact
                        path="/redemption"
                        name="Redemption"
                        element={<RedemptionProducts />}
                    />
                    <Route
                        exact
                        path="/redemption/:id"
                        name="Redemption Detail"
                        element={<RedemptionProductDetail />}
                    />
                    {/* The old My Wallet page is retired (client feedback, 21 Oct 2026): anything
                        still pointing at it lands on the coin history the homepage's
                        "Wallet History" link opens. */}
                    <Route exact path="/credit-details" element={<Navigate to="/profile/credit-transactions" replace />} />
                    <Route
                        exact
                        path="/credit-packages"
                        name="Credit Packages"
                        element={<CreditPackages />}
                    />
                    <Route
                        exact
                        path="/treasure-box"
                        name="Treasure Bag"
                        element={<TreasureBox />}
                    />
                    <Route
                        exact
                        path="/payment/result"
                        name="Payment Result"
                        element={<PaymentResult />}
                    />
                    <Route path="dashboard" name="Dashboard" element={<DefaultLayout />}>
                        {routes?.map((item, index) => (
                            <Route
                                key={index}
                                path={item?.path}
                                name={item?.name}
                                element={<item.element />}
                            />
                        ))}
                    </Route>
                    <Route path="/" name="Home" element={<Landing />} />
                    <Route path="*" name="Not Found" element={<Page404 />} />
                </Routes>
            {/* Support chat, docked on every page (hides itself on the full support pages). */}
            <SupportWidget />
            </Suspense>
        </BrowserRouter>
    )
}

export default App
